import { createBrowserRouter, type RouteObject } from "react-router";
import React from "react";

import { type CheckoutSession } from "@/types";
import { useCheckoutRedirectHandler } from "@/modules/checkout-session";
import { HandshakePage } from "@/modules/handshake/pages/handshake-page";
import { detectAppBasename } from "@/lib/app-base";
import { NotFound } from "./not-found";
import { RootHydrateFallback, RootLayout } from "./root-layout";
import { RouteError } from "./route-error";

type CheckoutPageProps = {
  onCheckoutFetched?: (session: CheckoutSession) => void;
};

function withCheckoutRedirect<T extends CheckoutPageProps>(
  Component: React.ComponentType<T>,
) {
  return function WrappedComponent(props: Omit<T, "onCheckoutFetched">) {
    const { handleCheckoutFetched } = useCheckoutRedirectHandler();
    return React.createElement(Component, {
      ...props,
      onCheckoutFetched: handleCheckoutFetched,
    } as T);
  };
}

/**
 * Page modules are loaded on demand so the entry bundle stays small: every
 * route only downloads its own chunk. The handshake entry (which just emits an
 * event and redirects to Auth) and the tiny not-found page stay static.
 * React Router resolves `lazy` before rendering the match, so in-app
 * navigations keep the current screen until the next chunk has arrived.
 */
function lazyPage<T extends CheckoutPageProps>(
  load: () => Promise<React.ComponentType<T>>,
): Pick<RouteObject, "lazy"> {
  return {
    lazy: async () => ({
      // The wrapper only takes the page's remaining (always optional) props.
      Component: withCheckoutRedirect(await load()) as React.ComponentType,
    }),
  };
}

const routes = [
  {
    path: "/",
    lazy: () =>
      import("@/modules/plan-selection/pages/plan-selection-page").then(
        (m) => ({ Component: m.PlanSelectionPage }),
      ),
  },
  // Entry: checkout.tamara.co/checkout/handshake/<checkoutId>?checkoutId=…
  // (basename /checkout). The id is also read from the query so the bare
  // /handshake?checkoutId=… form keeps working; the path form survives
  // merchants that strip the query string. Plan selection after the dwell
  // still lives under the /v2 mount.
  {
    path: "/handshake",
    Component: HandshakePage,
  },
  {
    path: "/handshake/:checkoutId",
    Component: HandshakePage,
  },
  {
    path: "/not-found",
    Component: NotFound,
  },
  {
    path: "/success",
    ...lazyPage(() =>
      import("@/modules/success/pages/success-page").then((m) => m.SuccessPage),
    ),
  },
  {
    path: "/ivr",
    ...lazyPage(() =>
      import("@/modules/ivr/pages/ivr-page").then((m) => m.IVRPage),
    ),
  },
  {
    path: "/confirmation",
    ...lazyPage(() =>
      import("@/modules/payment-confirmation/pages/confirmation-page").then(
        (m) => m.ConfirmationPage,
      ),
    ),
  },
  {
    path: "/early-repayment",
    ...lazyPage(() =>
      import("@/modules/early-repayment/pages/early-repayment-page").then(
        (m) => m.default,
      ),
    ),
  },
  {
    path: "/instalment/add-payment-method/*",
    ...lazyPage(() =>
      import("@/modules/payment-confirmation/pages/confirmation-page").then(
        (m) => m.ConfirmationPage,
      ),
    ),
  },
  {
    path: "/questionnaire",
    ...lazyPage(() =>
      import("@/modules/questionnaire/pages/questionnaire-pages").then(
        (m) => m.QuestionnaireRedirectPage,
      ),
    ),
  },
  {
    path: "/questionnaire/return",
    ...lazyPage(() =>
      import("@/modules/questionnaire/pages/questionnaire-pages").then(
        (m) => m.QuestionnaireReturnPage,
      ),
    ),
  },
  {
    path: "/kyc",
    ...lazyPage(() =>
      import("@/modules/kyc/pages/kyc-pages").then((m) => m.KycRedirectPage),
    ),
  },
  {
    path: "/kyc/return",
    ...lazyPage(() =>
      import("@/modules/kyc/pages/kyc-pages").then((m) => m.KycReturnPage),
    ),
  },
  {
    path: "/3ds",
    ...lazyPage(() =>
      import("@/modules/three-ds/pages/three-ds-redirect-page").then(
        (m) => m.ThreeDSRedirectPage,
      ),
    ),
  },
  {
    path: "/3ds/*",
    ...lazyPage(() =>
      import("@/modules/payment-confirmation/pages/confirmation-page").then(
        (m) => m.ConfirmationPage,
      ),
    ),
  },
] satisfies RouteObject[];

const basename = detectAppBasename();

// Every route gets the same boundary. Without one React Router renders its
// built-in developer error page (raw stack trace) straight at the customer,
// and the error never reaches New Relic. The pathless root layout owns the
// boot-shell hand-off and the hydrate fallback for lazily loaded entries.
export const router = createBrowserRouter(
  [
    {
      Component: RootLayout,
      HydrateFallback: RootHydrateFallback,
      ErrorBoundary: RouteError,
      children: routes.map((route) => ({
        ...route,
        ErrorBoundary: RouteError,
      })),
    },
  ],
  { basename },
);
